import { useEffect, useRef, useState, type FormEvent } from "react"
import { useLocation, useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"

export function DonorSigninPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationState = location.state as { email?: string; autoRequestOtp?: boolean } | null
  const [email, setEmail] = useState(navigationState?.email ?? "")
  const [code, setCode] = useState("")
  const [step, setStep] = useState<"request" | "verify">("request")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const autoRequestStarted = useRef(false)

  useEffect(() => {
    let cancelled = false

    void client.api.sponsor.portal.$get()
      .then((response) => {
        if (!cancelled && response.ok) {
          navigate("/donor", { replace: true })
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setCheckingSession(false)
      })

    return () => {
      cancelled = true
    }
  }, [navigate])

  const requestCode = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await client.api.sponsor.auth.request.$post({ json: { email } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return false
      }
      if (!("data" in body) || !body.data.sent) {
        setError("No sponsor account was found for this email. Submit a sponsorship application first.")
        return false
      }
      setStep("verify")
      return true
    } catch {
      setError("Could not send the code. Try again.")
      return false
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (checkingSession || !navigationState?.autoRequestOtp || autoRequestStarted.current || !email) return
    autoRequestStarted.current = true
    void requestCode().then(() => {
      navigate("/donor-signin", { replace: true, state: { email } })
    })
  }, [checkingSession, email, navigate, navigationState?.autoRequestOtp])

  const request = async (event: FormEvent) => {
    event.preventDefault()
    await requestCode()
  }

  const verify = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await client.api.sponsor.auth.verify.$post({ json: { email, code } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      navigate("/donor", { replace: true })
    } catch {
      setError("Could not verify the code. Try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (checkingSession) {
    return <div className="grid min-h-screen place-items-center bg-cream-50 text-sm text-ink/60">Checking your sponsor session…</div>
  }

  return (
    <div className="animate-fade-in-up min-h-screen bg-cream-50 text-ink">
      <main className="mx-auto grid min-h-screen w-full max-w-[1440px] items-center gap-12 px-5 py-10 sm:px-10 lg:grid-cols-[minmax(0,1fr)_576px] lg:gap-20 lg:px-20 lg:py-16">
        <section className="max-w-[610px]">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-cream-200 px-3 py-1.5 text-sm font-medium uppercase tracking-wide text-forest-700">
            <span className="h-1.5 w-1.5 rounded-full bg-forest-700" aria-hidden />
            Sponsor area
          </div>
          <h1 className="mt-7 max-w-[520px] font-display text-[42px] font-bold leading-[1.05] text-ink sm:text-[56px] lg:text-[64px]">
            Your sponsorship, <span className="text-forest-700">at a glance.</span>
          </h1>
          <p className="mt-5 max-w-[560px] text-base leading-7 text-ink/75 sm:text-lg">
            Sign in to see the children you support, read term updates from their schools, and manage your giving.
          </p>
          <ul className="mt-8 space-y-3 list-disc pl-5 text-sm leading-6 text-ink/65 marker:text-forest-700 sm:text-base">
            <li>Photos and report cards each term</li>
            <li>Receipts and giving history</li>
            <li>Pause, adjust, or switch to recurring</li>
          </ul>
        </section>

        <section className="rounded-[30px] border border-gold-500 bg-white p-6 sm:p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-forest-950">
              <img src="/images/home/logo.png" alt="" className="h-full w-full object-contain p-1.5" />
            </span>
            <div>
              <p className="font-display text-2xl font-bold leading-none text-ink">Redempta</p>
              <p className="mt-1 text-sm text-ink/55">Sponsor workspace</p>
            </div>
          </div>

          <div className="mt-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-forest-700">
              {step === "request" ? "Welcome back" : "Check your inbox"}
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">
              {step === "request" ? "Sign in to your sponsor workspace" : "Enter your access code"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink/65 sm:text-base">
              {step === "request"
                ? "Enter the email connected to your sponsorship and we'll send you a one-time code."
                : `Enter the 6-digit code sent to ${email}.`}
            </p>
          </div>

          {step === "request" ? (
            <form onSubmit={request} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">Sponsor email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="h-12 w-full rounded-full border border-gold-500 bg-cream-200/40 px-5 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700 focus:ring-2 focus:ring-forest-700/10"
                />
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-forest-950 px-5 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">6-digit code</span>
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  required
                  autoFocus
                  className="h-12 w-full rounded-full border border-gold-500 bg-cream-200/40 px-5 text-sm tracking-[0.35em] text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700 focus:ring-2 focus:ring-forest-700/10"
                />
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-forest-950 px-5 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Verifying…" : "Verify code"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCode("")
                  setStep("request")
                }}
                className="w-full rounded-full px-3 py-2 text-center text-sm font-medium text-forest-700 transition-colors hover:bg-cream-200"
              >
                Use a different email
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  )
}
